import { z } from 'zod';
import { getSupabase } from '../supabase.js';
import {
  SharedTabletOperatorsSchema,
  SharedTabletOperatorAuthorizationSchema,
  SharedTabletUnlockOkSchema,
  type SharedTabletOperator,
  type SharedTabletUnlockSuccess,
} from './sharedTabletOperatorTypes.js';
const UnlockInvalidSchema = z.object({
  status: z.literal('invalid_pin'),
  retry_after_seconds: z.number().int().nonnegative(),
});
const UnlockLockedSchema = z.object({
  status: z.literal('locked'),
  retry_after_seconds: z.number().int().nonnegative(),
});
const RawUnlockOkSchema = SharedTabletUnlockOkSchema.omit({
  app_role: true,
  account_user_id: true,
});
const UnlockResponseSchema = z.discriminatedUnion('status', [
  RawUnlockOkSchema,
  UnlockInvalidSchema,
  UnlockLockedSchema,
]);
type SharedTabletUnlockFailure =
  | z.infer<typeof UnlockInvalidSchema>
  | z.infer<typeof UnlockLockedSchema>;
export type SharedTabletUnlockResponse = SharedTabletUnlockSuccess | SharedTabletUnlockFailure;
export type { SharedTabletOperator, SharedTabletUnlockSuccess };

export const GeneratedOperatorPinSchema = z.object({
  tenant_personnel_id: z.string().uuid(),
  full_name: z.string().trim().min(1).max(200),
  pin: z.string().regex(/^[0-9]{4}$/),
  pin_version: z.number().int().positive(),
  identity_fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
  shown_once: z.literal(true),
  email_sent: z.boolean(),
  email_delivery: z.enum([
    'sent',
    'no_linked_account',
    'no_recipient_email',
    'recipient_lookup_failed',
    'email_not_configured',
    'send_failed',
  ]),
});
export type GeneratedOperatorPin = z.infer<typeof GeneratedOperatorPinSchema>;

export const RevealedOperatorPinSchema = z.object({
  tenant_personnel_id: z.string().uuid(),
  full_name: z.string().trim().min(1).max(200),
  pin: z.string().regex(/^[0-9]{4}$/),
  pin_version: z.number().int().positive(),
  identity_fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
  revealed: z.literal(true),
});
export type RevealedOperatorPin = z.infer<typeof RevealedOperatorPinSchema>;
export type VisibleOperatorPin = GeneratedOperatorPin | RevealedOperatorPin;

export function isOperatorPinNotRecoverable(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const row = error as Record<string, unknown>;
  return [row.message, row.details, row.hint]
    .some((value) => typeof value === 'string' && value.includes('operator_pin_not_recoverable'));
}

export function shouldProvisionOperatorPin(input: {
  wasActive: boolean | null;
  isActive: boolean;
}): boolean {
  return input.isActive && input.wasActive !== true;
}

export async function fetchSharedTabletOperators(): Promise<SharedTabletOperator[]> {
  const { data, error } = await getSupabase().rpc('shared_tablet_operators_v1');
  if (error) throw error;
  return SharedTabletOperatorsSchema.parse(data);
}

export async function fetchSharedTabletPinRequired(): Promise<boolean> {
  const { data, error } = await getSupabase().rpc('shared_tablet_pin_required_v1');
  if (error) throw error;
  if (typeof data !== 'boolean') throw new Error('shared_tablet_pin_policy_invalid');
  return data;
}

export async function setSharedTabletPinRequired(pinRequired: boolean): Promise<void> {
  const { data, error } = await getSupabase().rpc('set_shared_tablet_pin_required_v1', {
    p_pin_required: pinRequired,
  });
  if (error) throw error;
  if (data !== true) throw new Error('shared_tablet_pin_policy_not_saved');
}

export async function unlockSharedTabletOperator(input: {
  tenantPersonnelId: string;
  pin: string;
  deviceId: string;
}): Promise<SharedTabletUnlockResponse> {
  const { data, error } = await getSupabase().rpc('unlock_shared_tablet_operator_v1', {
    p_tenant_personnel_id: input.tenantPersonnelId,
    p_pin: input.pin,
    p_device_id: input.deviceId,
  });
  if (error) throw error;
  return enrichUnlockResponse(UnlockResponseSchema.parse(data), input);
}

export async function unlockSharedTabletOperatorWithoutPin(input: {
  tenantPersonnelId: string;
  deviceId: string;
}): Promise<SharedTabletUnlockResponse> {
  const { data, error } = await getSupabase().rpc('unlock_shared_tablet_operator_without_pin_v1', {
    p_tenant_personnel_id: input.tenantPersonnelId,
    p_device_id: input.deviceId,
  });
  if (error) throw error;
  return enrichUnlockResponse(UnlockResponseSchema.parse(data), input);
}

async function enrichUnlockResponse(
  response: z.infer<typeof UnlockResponseSchema>,
  input: { tenantPersonnelId: string; deviceId: string },
): Promise<SharedTabletUnlockResponse> {
  if (response.status !== 'ok') return response;
  const { data, error } = await getSupabase().rpc(
    'shared_tablet_operator_authorization_v1',
    {
      p_grant_id: response.grant_id,
      p_grant_token: response.grant_token,
      p_tenant_personnel_id: input.tenantPersonnelId,
      p_device_id: input.deviceId,
    },
  );
  if (error) throw error;
  const authorization = SharedTabletOperatorAuthorizationSchema.parse(data);
  return SharedTabletUnlockOkSchema.parse({ ...response, ...authorization });
}

export async function generateTenantPersonnelPin(
  tenantPersonnelId: string,
): Promise<GeneratedOperatorPin> {
  const { data, error } = await getSupabase().functions.invoke('send-operator-pin', {
    body: { tenant_personnel_id: tenantPersonnelId },
  });
  if (error) throw error;
  return GeneratedOperatorPinSchema.parse(data);
}

export async function revealTenantPersonnelPin(
  tenantPersonnelId: string,
): Promise<RevealedOperatorPin> {
  const { data, error } = await getSupabase().rpc('reveal_tenant_personnel_pin_v1', {
    p_tenant_personnel_id: tenantPersonnelId,
  });
  if (error) throw error;
  return RevealedOperatorPinSchema.parse(data);
}

export async function setMyTenantPersonnelPin(pin: string): Promise<GeneratedOperatorPin> {
  const { data, error } = await getSupabase().rpc('set_my_tenant_personnel_pin_v1', { p_pin: pin });
  if (error) throw error;
  return GeneratedOperatorPinSchema.parse({
    ...(data as Record<string, unknown>),
    email_sent: false,
    email_delivery: 'no_linked_account',
  });
}

export async function revokeTenantPersonnelPin(tenantPersonnelId: string): Promise<void> {
  const { error } = await getSupabase().rpc('revoke_tenant_personnel_pin_v1', {
    p_tenant_personnel_id: tenantPersonnelId,
  });
  if (error) throw error;
}

export async function activateSharedTabletOperatorGrant(input: {
  grantId: string;
  grantToken: string;
  tenantPersonnelId: string;
  deviceId: string;
}): Promise<void> {
  const { data, error } = await getSupabase().rpc(
    'activate_shared_tablet_operator_grant_v1',
    {
      p_grant_id: input.grantId,
      p_grant_token: input.grantToken,
      p_tenant_personnel_id: input.tenantPersonnelId,
      p_device_id: input.deviceId,
    },
  );
  if (error) throw error;
  if (data !== true) throw new Error('shared_tablet_operator_activation_failed');
}

export async function deactivateSharedTabletOperator(deviceId: string): Promise<void> {
  const { data, error } = await getSupabase().rpc(
    'deactivate_shared_tablet_operator_v1',
    { p_device_id: deviceId },
  );
  if (error) throw error;
  if (data !== true) throw new Error('shared_tablet_operator_deactivation_failed');
}
